import { Issue, CanvasPreview, FeaturePRD, Project } from "../../types/issues";
import { LabelInterface } from "../../types/labels";
import { User } from "../../types/users";
import { create } from "zustand";
import { SupabaseClient } from "@supabase/supabase-js";
import { onProjectsSnapshot } from "@/store/supabaseSlice";
import { KanbanDefaultStates } from "@/components/ProjectList/CanvasKanban";
import axios from "axios";
import store from "../store";
import { Canvas } from "@/utils/types";

type CanvasIdToIssue = Record<string, Issue>;

interface IssuesState {
  issuesByProject: Record<string, CanvasIdToIssue>;
  canvasIdToIssue: CanvasIdToIssue;
  currentCanvas: Record<string, Canvas>;
  _convertToIssuesByProject: (
    issues: Issue[]
  ) => Record<string, CanvasIdToIssue>;
  _createMergedIssuesByProject: (
    issuesByProjectToMerge: Record<string, CanvasIdToIssue>
  ) => Record<string, CanvasIdToIssue>;
  fetchCurrentCanvas: (
    supabaseClient: SupabaseClient | undefined,
    canvasId: string
  ) => void;
  fetchIssuesForProject: (
    supabaseClient: SupabaseClient | undefined,
    projectId: string,
    force: boolean
  ) => void;
  deleteIssue: (
    supabaseClient: SupabaseClient | undefined,
    id: string,
    sessionToken?: string
  ) => Promise<void>;
  bulkDeleteIssues: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    sessionToken?: string
  ) => Promise<void>;
  updateIssueStatus: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    statusId: string
  ) => void;
  updateIssuePriority: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    priority: number | null
  ) => void;
  updateIssueAssignee: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    assignee: User | null
  ) => void;
  createIssueLabel: (
    supabaseClient: SupabaseClient | undefined,
    project: Project,
    label: LabelInterface
  ) => Promise<void>;
  updateIssueLabels: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    labels: LabelInterface[]
  ) => void;
  updateIssueEstimate: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    estimate: number | null
  ) => void;
  updateIssueDueDate: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    dueDate: Date | null
  ) => void;
  bulkUpdateIssueAssignee: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    assignee: User | null
  ) => void;
  bulkUpdateIssuePriority: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    priority: number | null
  ) => void;
  bulkUpdateIssueStatus: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    statusId: string
  ) => void;
  bulkUpdateIssueEstimate: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    estimate: number | null
  ) => void;
  bulkUpdateIssueLabel: (
    supabaseClient: SupabaseClient | undefined,
    issueIdToLabels: { issueId: string; labels: LabelInterface[] }[]
  ) => void;
  bulkUpdateIssueDueDate: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    dueDate: string | null
  ) => void;
  bulkUpdateIssueOrder: (
    supabaseClient: SupabaseClient | undefined,
    prdsToUpdate: FeaturePRD[]
  ) => void;
  createCanvas: (
    supabaseClient: SupabaseClient | undefined,
    token: string,
    projectId: string,
    canvasPayload: Canvas
  ) => any;
  updateCanvas: (
    supabaseClient: SupabaseClient | undefined,
    canvasId: string,
    canvasPayload: Canvas
  ) => void;
  createFeaturePRD: (
    supabaseClient: SupabaseClient | undefined,
    featurePRDPayload: FeaturePRD
  ) => void;
  updateFeaturePRD: (
    supabaseClient: SupabaseClient | undefined,
    featurePRDPayload: FeaturePRD
  ) => void;
  upsertFeaturePRDs: (
    supabaseClient: SupabaseClient | undefined,
    featurePRDPayloads: FeaturePRD[]
  ) => void;
  syncFeaturePrdData: (featurePrds: FeaturePRD[]) => void;
  syncCanvasData: (canvases: Canvas[] | CanvasPreview[]) => void;
}

export const useIssuesStore = create<IssuesState>((set, get) => ({
  issuesByProject: {},
  canvasIdToIssue: {},
  currentCanvas: {},
  // helper method
  _convertToIssuesByProject: (issues: Issue[]) => {
    const projectToIssueMap: Record<string, CanvasIdToIssue> = {};
    issues.forEach((issue) => {
      projectToIssueMap[issue.canvas.project_id] = {
        ...projectToIssueMap[issue.canvas.project_id],
        [issue.canvas.id]: issue,
      };
    });

    return projectToIssueMap;
  },
  // helper method
  _createMergedIssuesByProject: (
    issuesByProjectToMerge: Record<string, CanvasIdToIssue>
  ) => {
    const state = get();

    return Object.fromEntries(
      Object.entries(issuesByProjectToMerge).map(
        ([key, canvasIdToIssueMap]) => [
          key,
          {
            ...state.issuesByProject[key],
            ...canvasIdToIssueMap,
          },
        ]
      )
    );
  },
  syncFeaturePrdData: (featurePrds: FeaturePRD[]) => {
    set((state) => {
      const updates = {};
      for (const prd of featurePrds) {
        const issue = state.canvasIdToIssue[prd?.canvas_id ?? ""];
        if (issue) {
          updates[prd.canvas_id!] = {
            ...issue,
            featurePrd: { ...issue.featurePrd, ...prd },
          };
        }
      }

      return {
        issuesByProject: {
          ...state.issuesByProject,
          ...state._createMergedIssuesByProject(
            state._convertToIssuesByProject(Object.values(updates))
          ),
        },
        canvasIdToIssue: { ...state.canvasIdToIssue, ...updates },
      };
    });
  },
  syncCanvasData: (canvases: Canvas[] | CanvasPreview[]) => {
    set((state) => {
      const updates = {};
      let updatedCurrentCanvas: Canvas | null = null;
      for (const canvas of canvases) {
        const issue = state.canvasIdToIssue[canvas.id];
        if (issue) {
          updates[canvas.id] = {
            ...issue,
            canvas: {
              ...issue.canvas,
              ...canvas,
            },
          };
          if (issue.canvas.id === state.currentCanvas[issue.canvas.id]?.id) {
            updatedCurrentCanvas = {
              ...state.currentCanvas[issue.canvas.id],
              ...canvas,
            };
          }
        }
      }

      if (updatedCurrentCanvas) {
        return {
          canvasIdToIssue: { ...state.canvasIdToIssue, ...updates },
          issuesByProject: {
            ...state.issuesByProject,
            ...state._createMergedIssuesByProject(
              state._convertToIssuesByProject(Object.values(updates))
            ),
          },
          currentCanvas: {
            ...state.currentCanvas,
            [updatedCurrentCanvas.id]: updatedCurrentCanvas,
          },
        };
      } else {
        return {
          canvasIdToIssue: { ...state.canvasIdToIssue, ...updates },
          issuesByProject: {
            ...state.issuesByProject,
            ...state._createMergedIssuesByProject(
              state._convertToIssuesByProject(Object.values(updates))
            ),
          },
          currentCanvas: {
            ...state.currentCanvas,
          },
        };
      }
    });
  },
  fetchIssuesForProject: async (
    supabaseClient: SupabaseClient | undefined,
    projectId: string,
    force: boolean = false
  ) => {
    const state = get();
    if (!supabaseClient) {
      console.warn("fetchIssuesForProject: Missing supabase client");
      return;
    }

    if (!force && state.issuesByProject[projectId]) {
      return;
    }

    const canvasPreviewsQuery = supabaseClient.rpc(
      "get_canvas_previews_filtered",
      {
        project_id_inp: projectId,
        env_inp: import.meta.env.VITE_ENVIRONMENT,
      }
    );

    const { data: canvasPreviewsData, error: canvasPreviewsError } =
      await canvasPreviewsQuery;

    if (canvasPreviewsError) {
      console.error("Error fetching canvas previews", canvasPreviewsError);
      return;
    }

    const featurePrdsQuery = supabaseClient
      .from("feature_prd")
      .select("*")
      .in(
        "canvas_id",
        canvasPreviewsData.map((canvas) => canvas.id)
      );

    const { data: featurePrdsData, error: featurePrdsError } =
      await featurePrdsQuery;

    if (featurePrdsError) {
      console.error("Error fetching feature prds", featurePrdsError);
      return;
    }

    // update the internal state
    let issues: CanvasIdToIssue = {};
    const canvasIdToFeaturePrd: Record<string, FeaturePRD> =
      featurePrdsData.reduce((acc, prd) => {
        acc[prd.canvas_id] = prd;
        return acc;
      }, {});

    canvasPreviewsData.forEach((canvas) => {
      let prd = canvasIdToFeaturePrd[canvas.id];
      if (prd) {
        // if prd status isnt valid, set it to backlog
        if (!Object.values(KanbanDefaultStates).includes(prd.status as any)) {
          prd.status = KanbanDefaultStates.BACKLOG;
        }
        issues[canvas.id] = new Issue(canvas, prd);
      } else {
        // if no prd, create a new issue with no prd
        issues[canvas.id] = new Issue(canvas, undefined);
      }
    });

    set({
      issuesByProject: {
        ...state.issuesByProject,
        [projectId]: issues,
      },
      canvasIdToIssue: {
        ...state.canvasIdToIssue,
        ...Object.fromEntries(
          Object.values(issues).map((issue) => [issue.canvas.id, issue])
        ),
      },
    });
  },
  bulkUpdateIssueEstimate: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    estimate: number | null
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    // Save original PRDs
    const originalPrds = issueIds
      .map((issueId) => {
        const issue = state.canvasIdToIssue[issueId];
        if (!issue) return null;
        return issue.featurePrd;
      })
      .filter((prd): prd is NonNullable<typeof prd> => prd !== null);

    // Create updated PRDs
    const updatedPrds = originalPrds.map((prd) => ({
      ...prd,
      estimate: estimate ?? undefined,
    }));

    // Update UI optimistically
    state.syncFeaturePrdData(updatedPrds);

    // Update Supabase
    supabaseClient
      .from("feature_prd")
      .update({
        estimate: estimate,
      })
      .in(
        "id",
        originalPrds.map((prd) => prd.id)
      )
      .then(({ error }) => {
        if (error) {
          console.error("Error updating estimate:", error);
          // Roll back to original state
          state.syncFeaturePrdData(originalPrds);
          return;
        }
      });
  },
  updateIssueEstimate: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    estimate: number | null
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    const issue = state.canvasIdToIssue[issueId];
    if (!issue) {
      return;
    }

    const featurePrd = issue.featurePrd;
    if (!featurePrd) {
      return;
    }

    // Create updated PRD
    const updatedPrd = {
      ...featurePrd,
      estimate: estimate ?? undefined,
    };

    // Update UI optimistically
    state.syncFeaturePrdData([updatedPrd]);

    // Update Supabase
    supabaseClient
      .from("feature_prd")
      .update({
        estimate: estimate,
      })
      .eq("id", featurePrd.id)
      .then(({ error }) => {
        if (error) {
          console.error("Error updating estimate:", error);
          // Roll back to original state
          state.syncFeaturePrdData([featurePrd]);
          return;
        }
      });
  },
  bulkDeleteIssues: async (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    sessionToken?: string
  ) => {
    const state = get();

    if (!supabaseClient) {
      return;
    }

    // filter out the issues that are not in the current project
    const issuesToKeep = Object.values(state.canvasIdToIssue).filter(
      (issue) => !issueIds.includes(issue.canvas.id)
    );

    // update the internal state by removing the issues from the projectIssues
    set({
      issuesByProject: {
        ...state.issuesByProject,
        ...Object.fromEntries(
          Object.entries(state._convertToIssuesByProject(issuesToKeep)).map(
            ([key, value]) => [key, value]
          )
        ),
      },
      canvasIdToIssue: {
        ...state.canvasIdToIssue,
        ...Object.fromEntries(
          issuesToKeep.map((issue) => [issue.canvas.id, issue])
        ),
      },
    });

    try {
      // Soft delete the canvas by setting deleted_at
      const { error: updateError } = await supabaseClient
        .from("canvases")
        .update({
          deleted_at: new Date().toISOString(),
        })
        .in("id", issueIds);

      if (updateError) {
        console.error("Error deleting issue:", updateError);
        throw updateError;
      }

      // Hard delete the associated feature PRD
      const { error: prdDeleteError } = await supabaseClient
        .from("feature_prd")
        .delete()
        .in("canvas_id", issueIds);

      if (prdDeleteError) {
        console.error("Error deleting issue:", prdDeleteError);
        throw prdDeleteError;
      }

      // Update Redux store
      issueIds.forEach((id) => {
        axios
          .delete(
            `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${id}/cache`,
            {
              headers: {
                Authorization: `Bearer ${sessionToken}`,
              },
            }
          )
          .catch((err) => {
            console.error("Error deleting canvas cache:", err.toString());
          });
      });
    } catch (error) {
      console.error("Error deleting issue:", error);
      // re-insert the issues

      set({
        issuesByProject: {
          ...state.issuesByProject,
        },
        canvasIdToIssue: {
          ...state.canvasIdToIssue,
        },
      });
      return;
    }
  },
  deleteIssue: async (
    supabaseClient: SupabaseClient | undefined,
    id: string,
    sessionToken?: string
  ) => {
    const state = get();

    if (!supabaseClient) {
      return;
    }

    // update the internal state by removing the issue from the projectIssues
    const issuesToKeep = Object.values(state.canvasIdToIssue).filter(
      (issue) => issue.canvas.id !== id
    );
    set({
      issuesByProject: {
        ...state.issuesByProject,
        ...state._createMergedIssuesByProject(
          state._convertToIssuesByProject(issuesToKeep)
        ),
      },
      canvasIdToIssue: {
        ...state.canvasIdToIssue,
        ...Object.fromEntries(
          issuesToKeep.map((issue) => [issue.canvas.id, issue])
        ),
      },
    });

    try {
      // Soft delete the canvas by setting deleted_at
      const { error: updateError } = await supabaseClient
        .from("canvases")
        .update({
          deleted_at: new Date().toISOString(),
        })
        .eq("id", id);

      if (updateError) {
        console.error("Error deleting issue:", updateError);
        throw updateError;
      }

      // Hard delete the associated feature PRD
      const { error: prdDeleteError } = await supabaseClient
        .from("feature_prd")
        .delete()
        .eq("canvas_id", id);

      if (prdDeleteError) {
        console.error("Error deleting issue:", prdDeleteError);
        throw prdDeleteError;
      }

      axios
        .delete(
          `${import.meta.env.VITE_BACKEND_BASE_URL}/canvases/${id}/cache`,
          {
            headers: {
              Authorization: `Bearer ${sessionToken}`,
            },
          }
        )
        .catch((err) => {
          console.error("Error deleting canvas cache:", err.toString());
        });
    } catch (error) {
      console.error("Error deleting issue:", error);
      // re-insert the issue
      set({
        issuesByProject: {
          ...state.issuesByProject,
        },
        canvasIdToIssue: {
          ...state.canvasIdToIssue,
        },
      });
      return;
    }
  },
  bulkUpdateIssueStatus: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    statusId: string
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    // Save original PRDs
    const originalPrds = issueIds
      .map((issueId) => {
        const issue = state.canvasIdToIssue[issueId];
        if (!issue) return null;
        return issue.featurePrd;
      })
      .filter((prd): prd is NonNullable<typeof prd> => prd !== null);

    // get status key
    const statusKey = Object.entries(KanbanDefaultStates).find(
      ([_, value]) => value === statusId
    )?.[0];

    if (!statusKey) {
      console.error("Could not find status key for", statusId);
      return;
    }

    // Create updated PRDs
    const updatedPrds = originalPrds.map((prd) => ({
      ...prd,
      status: KanbanDefaultStates[statusKey],
    }));

    // Update UI optimistically
    state.syncFeaturePrdData(updatedPrds);

    // update supabase
    supabaseClient
      .from("feature_prd")
      .update({
        status: KanbanDefaultStates[statusKey],
      })
      .in(
        "id",
        originalPrds.map((prd) => prd.id)
      )
      .then(({ error }) => {
        if (error) {
          console.error("Error updating status:", error);
          // Roll back to original state
          state.syncFeaturePrdData(originalPrds);
          return;
        }
      });
  },
  updateIssueStatus: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    statusId: string
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    const issue = state.canvasIdToIssue[issueId];
    if (!issue) {
      return;
    }

    // Save original PRD
    const originalPrd = issue.featurePrd;
    if (!originalPrd) {
      return;
    }

    // Find the matching KanbanDefaultStates value
    const statusKey = Object.entries(KanbanDefaultStates).find(
      ([_, value]) => value === statusId
    )?.[0];

    if (!statusKey) {
      console.error("Could not find status key for", statusId);
      return;
    }

    // Create updated PRD
    const updatedPrd = {
      ...originalPrd,
      status: KanbanDefaultStates[statusKey],
    };

    // Update UI optimistically
    state.syncFeaturePrdData([updatedPrd]);

    // Update Supabase
    supabaseClient
      .from("feature_prd")
      .update({
        status: KanbanDefaultStates[statusKey],
      })
      .eq("id", originalPrd.id)
      .then(({ error }) => {
        if (error) {
          console.error("Error updating status:", error);
          // Roll back to original state
          state.syncFeaturePrdData([originalPrd]);
          return;
        }
      });
  },
  bulkUpdateIssuePriority: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    priority: number | null
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    // Save original PRDs
    const originalPrds = issueIds
      .map((issueId) => {
        const issue = state.canvasIdToIssue[issueId];
        if (!issue) return null;
        return issue.featurePrd;
      })
      .filter((prd): prd is NonNullable<typeof prd> => prd !== null);

    // Create updated PRDs
    const updatedPrds = originalPrds.map((prd) => ({
      ...prd,
      priority: priority ?? undefined,
    }));

    // Update UI optimistically
    state.syncFeaturePrdData(updatedPrds);

    // update supabase
    supabaseClient
      .from("feature_prd")
      .update({
        priority: priority,
      })
      .in(
        "id",
        originalPrds.map((prd) => prd.id)
      )
      .then(({ error }) => {
        if (error) {
          console.error("Error updating priority:", error);
          // Roll back to original state
          state.syncFeaturePrdData(originalPrds);
          return;
        }
      });
  },
  updateIssuePriority: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    priority: number | null
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    const issue = state.canvasIdToIssue[issueId];
    if (!issue) {
      return;
    }

    // Save original PRD
    const originalPrd = issue.featurePrd;
    if (!originalPrd) {
      return;
    }

    // Create updated PRD
    const updatedPrd = {
      ...originalPrd,
      priority: priority !== null ? Number(priority) : undefined,
    };

    // Update UI optimistically
    state.syncFeaturePrdData([updatedPrd]);

    // Update Supabase
    supabaseClient
      .from("feature_prd")
      .update({
        priority: priority !== null ? Number(priority) : null,
      })
      .eq("id", originalPrd.id)
      .then(({ error }) => {
        if (error) {
          console.error("Error updating priority:", error);
          // Roll back to original state
          state.syncFeaturePrdData([originalPrd]);
          return;
        }
      });
  },
  bulkUpdateIssueAssignee: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    assignee: User | null
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    // Get and save original canvases
    const originalCanvases = issueIds
      .map((issueId) => {
        const issue = state.canvasIdToIssue[issueId];
        if (!issue) return null;
        return issue.canvas;
      })
      .filter(
        (canvas): canvas is NonNullable<typeof canvas> => canvas !== null
      );

    // Create updated canvases
    const updatedCanvases = originalCanvases.map((canvas) => ({
      ...canvas,
      assigned_user_id: assignee?.id || null,
      last_updated: new Date().toISOString(),
    }));

    // Update UI optimistically
    state.syncCanvasData(updatedCanvases);

    // Update Supabase
    supabaseClient
      .from("canvases")
      .update({
        assigned_user_id: assignee?.id || null,
      })
      .in("id", issueIds)
      .select()
      .then(({ error }) => {
        if (error) {
          console.error("Error updating assignee:", error);
          // Roll back to original state
          state.syncCanvasData(originalCanvases);
          return;
        }
      });
  },
  updateIssueAssignee: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    assignee: User | null
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    const issue = state.canvasIdToIssue[issueId];
    if (!issue) {
      return;
    }

    // Save original canvas
    const originalCanvas = issue.canvas;
    if (!originalCanvas) {
      return;
    }

    // Create updated canvas
    const updatedCanvas = {
      ...originalCanvas,
      assigned_user_id: assignee?.id || null,
      last_updated: new Date().toISOString(),
    };

    // Update UI optimistically
    state.syncCanvasData([updatedCanvas]);

    // Update Supabase
    supabaseClient
      ?.from("canvases")
      .update({
        assigned_user_id: assignee?.id || null,
      })
      .eq("id", updatedCanvas.id)
      .then(({ error }) => {
        if (error) {
          console.error("Error updating assignee:", error);
          // Roll back to original state
          state.syncCanvasData([originalCanvas]);
          return;
        }
      });
  },
  createIssueLabel: async (
    supabaseClient: SupabaseClient | undefined,
    project: Project,
    label: LabelInterface
  ) => {
    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    try {
      // Label goes into the project table, in the labels column
      const formattedLabel = {
        id: label.id,
        name: label.label.trim(),
        label: label.label.trim(),
        color: {
          key: label.label.toLowerCase().replace(/\s+/g, "-"),
          label: label.label.trim(),
          value: label.color,
        },
      };

      // convert labels JSON to array
      const currentProjectLabels =
        JSON.parse(JSON.stringify(project.labels)) || [];

      const updatedProjectLabels = [...currentProjectLabels, formattedLabel];

      const addLabelResponse = await supabaseClient
        ?.from("projects")
        .update({
          labels: updatedProjectLabels,
        })
        .eq("id", project.id)
        .select()
        .single();

      if (!addLabelResponse || addLabelResponse.error) {
        return;
      }

      // Update Redux store with the new project data
      const updatedProject = addLabelResponse.data;
      store.dispatch(
        onProjectsSnapshot({
          newItems: [updatedProject],
          deletedItems: [],
        })
      );
    } catch (error) {}
  },
  bulkUpdateIssueLabel: (
    supabaseClient: SupabaseClient | undefined,
    issueIdToLabels: { issueId: string; labels: LabelInterface[] }[]
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    // Save original canvases
    const originalCanvases = issueIdToLabels
      .map(({ issueId }) => {
        const issue = state.canvasIdToIssue[issueId];
        if (!issue) return null;
        return issue.canvas;
      })
      .filter(
        (canvas): canvas is NonNullable<typeof canvas> => canvas !== null
      );

    // Create updated canvases
    const updatedCanvases = originalCanvases.map((canvas, index) => ({
      ...canvas,
      labels: issueIdToLabels[index].labels.map((label) => label.id),
    }));

    // Update UI optimistically
    state.syncCanvasData(updatedCanvases);

    const updatePromises = issueIdToLabels.map((issueIdAndLabel) => {
      const issueId = issueIdAndLabel.issueId;
      const labelIDs = issueIdAndLabel.labels.map((label) => label.id);

      return supabaseClient
        .from("canvases")
        .update({ labels: labelIDs })
        .eq("id", issueId)
        .select()
        .single();
    });

    Promise.all(updatePromises)
      .then((results) => {
        const errors = results.filter((result) => result.error);
        if (errors.length > 0) {
          console.error("Errors in bulk update labels:", errors);
          // Roll back to original state
          state.syncCanvasData(originalCanvases);
        }
      })
      .catch((error) => {
        console.error("Error in bulk update labels:", error);
        // Roll back to original state
        state.syncCanvasData(originalCanvases);
      });
  },
  updateIssueLabels: async (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    labels: LabelInterface[]
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    const issue = state.canvasIdToIssue[issueId];
    if (!issue) {
      return;
    }

    // Save original canvas
    const originalCanvas = issue.canvas;
    if (!originalCanvas) {
      return;
    }

    // Create updated canvas
    const updatedCanvas = {
      ...originalCanvas,
      labels: labels.map((label) => label.id),
    };

    // Update UI optimistically
    state.syncCanvasData([updatedCanvas]);

    // Update Supabase
    const response = await supabaseClient
      ?.from("canvases")
      .update({
        labels: labels.map((label) => label.id),
      })
      .eq("id", originalCanvas.id)
      .select()
      .single();

    if (!response || response.error) {
      // Roll back to original state
      state.syncCanvasData([originalCanvas]);
      return;
    }
  },
  bulkUpdateIssueDueDate: (
    supabaseClient: SupabaseClient | undefined,
    issueIds: string[],
    dueDate: string | null
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    // Save original PRDs
    const originalPrds = issueIds
      .map((issueId) => {
        const issue = state.canvasIdToIssue[issueId];
        if (!issue) return null;
        return issue.featurePrd;
      })
      .filter((prd): prd is NonNullable<typeof prd> => prd !== null);

    // Create updated PRDs
    const updatedPrds = originalPrds.map((prd) => ({
      ...prd,
      due_date: dueDate ?? undefined,
    }));

    // Update UI optimistically
    state.syncFeaturePrdData(updatedPrds);

    supabaseClient
      .from("feature_prd")
      .update({
        due_date: dueDate,
      })
      .in(
        "id",
        originalPrds.map((prd) => prd.id)
      )
      .then(({ error }) => {
        if (error) {
          console.error("Error updating due date:", error);
          // Roll back to original state
          state.syncFeaturePrdData(originalPrds);
          return;
        }
      });
  },
  updateIssueDueDate: (
    supabaseClient: SupabaseClient | undefined,
    issueId: string,
    dueDate: Date | null
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    const issue = state.canvasIdToIssue[issueId];
    if (!issue) {
      return;
    }

    // Save original PRD
    const originalPrd = issue.featurePrd;
    if (!originalPrd) {
      return;
    }

    // Create updated PRD
    const updatedPrd = {
      ...originalPrd,
      due_date: dueDate?.toISOString() ?? undefined,
    };

    // Update UI optimistically
    state.syncFeaturePrdData([updatedPrd]);

    // Update Supabase
    supabaseClient
      .from("feature_prd")
      .update({
        due_date: dueDate?.toISOString() ?? null,
      })
      .eq("id", originalPrd.id)
      .then(({ error }) => {
        if (error) {
          console.error("Error updating due date:", error);
          // Roll back to original state
          state.syncFeaturePrdData([originalPrd]);
          return;
        }
      });
  },
  bulkUpdateIssueOrder: (
    supabaseClient: SupabaseClient | undefined,
    prdsToUpdate: FeaturePRD[]
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    if (prdsToUpdate.length === 0) {
      return;
    }

    // Save original PRDs
    const originalPrds = prdsToUpdate
      .map((prd) => {
        const issue = state.canvasIdToIssue[prd.canvas_id ?? ""];
        if (!issue) return null;
        return issue.featurePrd;
      })
      .filter((prd): prd is NonNullable<typeof prd> => prd !== null);

    // Create updated PRDs
    const updatedPrds = prdsToUpdate.map((prd) => ({
      ...prd,
    }));

    // Update UI optimistically
    state.syncFeaturePrdData(updatedPrds);

    supabaseClient
      .from("feature_prd")
      .upsert(prdsToUpdate, { onConflict: "canvas_id" })
      .then(({ error }) => {
        if (error) {
          console.error("Error updating issue order:", error);
          // Roll back to original state
          if (originalPrds.length > 0) {
            state.syncFeaturePrdData(originalPrds);
          }
          return;
        }
      });
  },
  createCanvas: async (
    supabaseClient: SupabaseClient | undefined,
    token: string,
    projectId: string,
    canvasPayload: Canvas
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    try {
      const res = await axios.post(
        `${import.meta.env.VITE_BACKEND_BASE_URL}/projects/${projectId}/canvas`,
        canvasPayload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res.data.canvas) {
        set({
          issuesByProject: {
            ...state.issuesByProject,
            [res.data.canvas.project_id ?? ""]: {
              ...state.issuesByProject[res.data.canvas.project_id ?? ""],
              [res.data.canvas.id]: new Issue(res.data.canvas, undefined),
            },
          },
          canvasIdToIssue: {
            ...state.canvasIdToIssue,
            [res.data.canvas.id]: new Issue(res.data.canvas, undefined),
          },
        });
      }

      return res.data.canvas;
    } catch (error) {
      console.error("Error creating canvas:", error);
      // nothing to roll back
      return null;
    }
  },
  fetchCurrentCanvas: (
    supabaseClient: SupabaseClient | undefined,
    canvasId: string
  ): void => {
    const state = get();

    if (!supabaseClient || !canvasId) {
      console.error("supabase or canvas id not initialized");
      return;
    }

    const currentCanvas = state.currentCanvas[canvasId];
    if (currentCanvas) {
      return;
    }

    // fetch the canvas from the db
    supabaseClient
      .from("canvases")
      .select("*")
      .eq("id", canvasId)
      .single()
      .then(({ data, error }) => {
        if (error) {
          console.error("Error fetching canvas:", error);
          return;
        }

        set({
          currentCanvas: {
            [canvasId]: data,
          },
        });
      });
  },
  updateCanvas: (
    supabaseClient: SupabaseClient | undefined,
    canvasId: string,
    canvasPayload: Canvas
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    const issue = state.canvasIdToIssue[canvasId];
    if (!issue) {
      return;
    }

    // Save original state
    const originalCanvas = issue.canvas;
    const originalCurrentCanvas = state.currentCanvas[canvasId];

    // convert canvasPayload to a CanvasPreview
    const payloadAsPreview: Partial<CanvasPreview> = {
      id: canvasPayload.id,
      assigned_user_id: canvasPayload.assigned_user_id ?? "",
      labels: canvasPayload.labels as string[],
      project_id: canvasPayload.project_id ?? "",
      env: canvasPayload.env ?? "",
      current_branch: canvasPayload.current_branch ?? "",
      owner_user_id: canvasPayload.owner_user_id ?? "",
      last_updated: canvasPayload.last_updated ?? "",
    };

    // merge canvasPayload with canvas
    const mergedCanvasPreview = {
      ...issue.canvas,
      ...payloadAsPreview,
    };

    // Update UI optimistically
    state.syncCanvasData([mergedCanvasPreview]);

    if (
      state.currentCanvas &&
      state.currentCanvas[canvasId]?.id === mergedCanvasPreview.id
    ) {
      set({
        currentCanvas: {
          [canvasId]: canvasPayload,
        },
      });
    }

    supabaseClient
      .from("canvases")
      .update(canvasPayload)
      .eq("id", canvasId)
      .then(({ error }) => {
        if (error) {
          console.error("Error updating canvas:", error);
          // Roll back to original state
          state.syncCanvasData([originalCanvas]);
          if (originalCurrentCanvas) {
            set({
              currentCanvas: {
                [canvasId]: originalCurrentCanvas,
              },
            });
          }
          return;
        }
      });
  },
  createFeaturePRD: (
    supabaseClient: SupabaseClient | undefined,
    featurePRDPayload: FeaturePRD
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    if (!featurePRDPayload.canvas_id) {
      console.error("Canvas ID is required");
      return;
    }

    if (!featurePRDPayload.id) {
      console.error("Feature PRD ID is required");
      return;
    }

    // Store current state before update
    const originalIssue = state.canvasIdToIssue[featurePRDPayload.canvas_id];

    // Update UI optimistically
    state.syncFeaturePrdData([featurePRDPayload]);

    supabaseClient
      .from("feature_prd")
      .insert(featurePRDPayload)
      .then(({ error }) => {
        if (error) {
          console.error("Error creating feature PRD:", error);
          // Roll back to original state if we had one
          if (originalIssue?.featurePrd) {
            state.syncFeaturePrdData([originalIssue.featurePrd]);
          }
          return;
        }
      });
  },
  updateFeaturePRD: (
    supabaseClient: SupabaseClient | undefined,
    featurePRDPayload: FeaturePRD
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    if (!featurePRDPayload.canvas_id) {
      console.error("Canvas ID is required");
      return;
    }

    if (!featurePRDPayload.id) {
      console.error("Feature PRD ID is required");
      return;
    }

    // Get existing PRD to merge with updates
    const issue = state.canvasIdToIssue[featurePRDPayload.canvas_id];
    if (!issue) {
      return;
    }

    const originalPrd = issue.featurePrd;
    if (!originalPrd) {
      return;
    }

    // Create updated PRD
    const updatedPrd = {
      ...originalPrd,
      ...featurePRDPayload,
    };

    // Update UI optimistically
    state.syncFeaturePrdData([updatedPrd]);

    supabaseClient
      .from("feature_prd")
      .update(featurePRDPayload)
      .eq("canvas_id", featurePRDPayload.canvas_id)
      .then(({ error }) => {
        if (error) {
          console.error("Error updating feature PRD:", error);
          // Roll back to original state
          state.syncFeaturePrdData([originalPrd]);
          return;
        }
      });
  },
  upsertFeaturePRDs: (
    supabaseClient: SupabaseClient | undefined,
    featurePRDPayloads: FeaturePRD[]
  ) => {
    const state = get();

    if (!supabaseClient) {
      console.error("Supabase client not initialized");
      return;
    }

    // Get existing PRDs to merge with updates and save originals
    const originalPrds = featurePRDPayloads
      .map((payload) => {
        const issue = state.canvasIdToIssue[payload.canvas_id ?? ""];
        if (!issue) return null;
        return issue.featurePrd;
      })
      .filter((prd): prd is NonNullable<typeof prd> => prd !== null);

    // Create updated PRDs
    const updatedPrds = featurePRDPayloads.map((payload) => {
      const issue = state.canvasIdToIssue[payload.canvas_id ?? ""];
      if (!issue) return payload;
      const existingPrd = issue.featurePrd;
      if (!existingPrd) return payload;
      return {
        ...existingPrd,
        ...payload,
      };
    });

    // Update UI optimistically
    state.syncFeaturePrdData(updatedPrds);

    supabaseClient
      .from("feature_prd")
      .upsert(featurePRDPayloads, { onConflict: "canvas_id" })
      .then(({ error }) => {
        if (error) {
          console.error("Error upserting feature PRDs:", error);
          // Roll back to original state
          if (originalPrds.length > 0) {
            state.syncFeaturePrdData(originalPrds);
          }
          return;
        }
      });
  },
}));
